import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { imageSetAvatarThunk } from "../image/imageSetAvatar";
import { ObjectTarget } from "../../app/permissions";
import { classroomInsert, classroomUpdate } from "./classroomInsUpdSlice";
import { UserInfo } from "../user/userSlice";

export enum ClassroomStatus {
    ENABLED     =  0,
	DONE    	=  1,
    NOT_READY   =  2,
}

export enum ClassroomHomework {
    YES          =  0,
    NO           =  1,
}

export interface ClassroomState {
    state: LoadStateInfo;
    selected?: ClassroomInfo;
}

const initialState: ClassroomState = {
    state: notStarted(),
};


export interface RetrieveClassroomRequest {
    classroomId: string;
}

export const retrieveClassroom = createAsyncThunk(
    "retrieveClassroom",
    async (param: RetrieveClassroomRequest, thunkAPI) => {
        console.debug("retrieve classroom");

        const response = await fetch(`${getBackendUrl()}/api/classroom/${param.classroomId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveClassroomRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.classroom;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveClassroom skipped: ${status.state.state}`);
            return false;
        }
    }
);


export const classroomSlice = createSlice({
    name: "classroom",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<string>) => {
            
            if (state.selected?.id !== action.payload) {
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        select: (state, action: PayloadAction<ClassroomInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.name}`);
            state.selected = action.payload;
        },
        clean: (state) => {
            state.state = notStarted();
            state.selected = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveClassroom.pending, (state, /* action */) => {
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveClassroom.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveClassroom.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(imageSetAvatarThunk.fulfilled, (state, action) => {
                if (state.selected
                    && action.meta.arg.objectTarget == ObjectTarget.CLASSROOM
                    && action.meta.arg.targetId == state.selected?.id
                ) {
                    state.selected.url = action.payload;
                }
            })
            .addCase(classroomInsert.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state =  loaded();
                state.selected = action.payload;
            })
            .addCase(classroomUpdate.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state =  loaded();
                state.selected = action.payload;
            })
            ;
    },
});

export interface ClassroomInfo {
    id: string;
    name: string;
    year?: string;
    url?: string;
    schoolId: string;
    routineId?: string;
    classroomStatus?: ClassroomStatus;
    imageProcessorStyle?: number;
    homework?: number;
    teachers?: UserInfo[]
}

export function getClassroomStatusName(status?: ClassroomStatus): string {
    switch (status) {
        case ClassroomStatus.ENABLED:
            return "Ativa";
        case ClassroomStatus.DONE:
            return "Finalizada";
        case ClassroomStatus.NOT_READY:
            return "Cadastro em andamento";
        default:
            return "Desconhecido";
    }
}

export function getClassroomHomeworkName(homework?: number): string {
    if (homework === undefined) {
        homework = ClassroomHomework.NO;
    }
    switch (homework) {
        case ClassroomHomework.YES:
            return "Ativa";
        default:
            return "Inativa";
    }
}

// export const { } = classroomSlice.actions;

export const selectClassroom = (state: RootState) => state.classroom;

export default classroomSlice.reducer;
